import type { PaidSubscriptionSnapshotDTO } from "@ai-crm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { paymentKeys, usePaymentScope } from "../../lib/payment-ui";
import { useState } from "react";
import type { FormEvent } from "react";
import { toast } from "sonner";
import type { AdminAccount } from "../../lib/api";
import { ApiError, adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import {
  formatMoscowDateTime,
  formatRubTotal,
  formatTokens,
} from "../../lib/format";
import { BillingOperationPanel } from "./billing-operation-panel";
import { BillingTariffChangePanel } from "./billing-tariff-change-panel";

const buttonClass = "rounded-md border px-3 py-2 text-sm disabled:opacity-50";

function Metric({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-1 font-medium">{children}</dd>
    </div>
  );
}

function PaidSnapshot({
  title,
  snapshot,
  priceLabel,
}: {
  title: string;
  snapshot: PaidSubscriptionSnapshotDTO | null;
  priceLabel: string;
}) {
  if (!snapshot) return null;
  return (
    <section className="rounded-md border p-3 text-sm">
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-2 break-words">
        {snapshot.tariffName} ({snapshot.tariffId}), каталог v
        {snapshot.catalogVersion}
      </p>
      <p>
        Базовый объём: {formatTokens(snapshot.baseMonthlyDialogs)}; оплаченный
        месячный объём: {formatTokens(snapshot.monthlyDialogs)}
      </p>
      <p>
        {priceLabel}: {snapshot.unitPriceRub} ₽ за диалог
      </p>
      <p>
        Срок исходной покупки: {snapshot.months} мес. × 30 дней; скидка:{" "}
        {snapshot.discountPercent}%
      </p>
      <p>Базовая стоимость месяца: {snapshot.baseMonthlyRub} ₽</p>
      <p>
        Стоимость исходной покупки: {formatRubTotal(snapshot.purchaseTotalRub)}
      </p>
    </section>
  );
}

export function BillingTab({ account }: { account: AdminAccount }) {
  const { token, admin } = useAdminAuth();
  return (
    <BillingWorkspace
      key={`${admin?.id}:${token}:${account.id}`}
      account={account}
    />
  );
}

function BillingWorkspace({ account }: { account: AdminAccount }) {
  const scope = usePaymentScope(account.id);
  const paymentSummary = useQuery({
    queryKey: [...paymentKeys.account(scope.adminId, account.id), "summary"],
    queryFn: ({ signal }) =>
      scope.read(signal, () =>
        adminApi.accountPayments(
          scope.token!,
          account.id,
          { limit: 1 },
          signal,
        ),
      ),
    enabled: Boolean(scope.token),
  });
  const { token } = useAdminAuth();
  const client = useQueryClient();
  const billing = useQuery({
    queryKey: ["admin", "account", account.id, "billing"],
    queryFn: () => adminApi.accountBilling(token!, account.id),
    enabled: Boolean(token && account.id),
  });
  const catalog = useQuery({
    queryKey: ["admin", "tariffs"],
    queryFn: () => adminApi.tariffs(token!),
    enabled: Boolean(token),
  });
  const [suspensionReason, setSuspensionReason] = useState("");
  const [statusPending, setStatusPending] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  async function invalidateAccount() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["admin", "account", account.id] }),
      client.invalidateQueries({ queryKey: ["admin", "accounts"] }),
      client.invalidateQueries({
        queryKey: ["admin", "account", account.id, "audit"],
      }),
    ]);
  }

  async function suspend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (statusPending) return;
    const reason = suspensionReason.trim();
    if (!reason) {
      setStatusError("Укажите причину приостановки");
      return;
    }
    setStatusPending(true);
    setStatusError(null);
    try {
      await adminApi.suspendAccount(token!, account.id, {
        expectedSettingsVersion: account.settingsVersion,
        reason,
      });
      setSuspensionReason("");
      toast.success("Аккаунт приостановлен");
      await invalidateAccount();
    } catch (caught) {
      setStatusError(
        caught instanceof ApiError && caught.status === 409
          ? "Настройки аккаунта изменились. Загрузите актуальную версию."
          : "Не удалось приостановить аккаунт.",
      );
    } finally {
      setStatusPending(false);
    }
  }

  async function resume() {
    if (statusPending) return;
    setStatusPending(true);
    setStatusError(null);
    try {
      await adminApi.resumeAccount(token!, account.id, {
        expectedSettingsVersion: account.settingsVersion,
      });
      toast.success("Работа аккаунта возобновлена");
      await invalidateAccount();
    } catch (caught) {
      setStatusError(
        caught instanceof ApiError && caught.status === 409
          ? "Настройки аккаунта изменились. Загрузите актуальную версию."
          : "Не удалось возобновить аккаунт.",
      );
    } finally {
      setStatusPending(false);
    }
  }

  const view = billing.data;
  const original = view?.subscription.originalPaidSnapshot;
  const effective = view?.subscription.effectivePaidSnapshot;
  const upgraded = Boolean(
    original &&
    effective &&
    (original.tariffId !== effective.tariffId ||
      original.baseMonthlyDialogs !== effective.baseMonthlyDialogs ||
      original.monthlyDialogs !== effective.monthlyDialogs ||
      original.unitPriceRub !== effective.unitPriceRub ||
      original.baseMonthlyRub !== effective.baseMonthlyRub),
  );
  const currentCatalogTariff = catalog.data?.definition.paidTariffs.find(
    (item) => item.id === view?.tariff.id,
  );
  return (
    <div className="space-y-4">
      <section className="panel space-y-3 p-4">
        <h2 className="text-lg font-semibold">Подписка и использование</h2>
        <p className="text-sm text-muted-foreground">
          Расчёт по московскому времени. {view?.quotaWindow === "TRIAL_TOTAL"
            ? "Пробный период имеет один общий запас диалогов на весь срок."
            : "Один период — ровно 30 дней."} Диалог
          учитывается после успешного принятия отправки Wazzup.
        </p>
        {billing.isPending && <p role="status">Загружаем подписку...</p>}
        {billing.isError && !view && (
          <div role="alert">
            Не удалось загрузить подписку.{" "}
            <button
              className={buttonClass}
              type="button"
              onClick={() => void billing.refetch()}
            >
              Повторить
            </button>
          </div>
        )}
        {view && (
          <>
            <p className="break-words font-semibold">
              {view.tariff.name} ({view.tariff.id}) ·{" "}
              {view.subscription.kind === "PAID" ? "оплаченная" : view.subscription.kind === "TRIAL" ? "пробная" : "Freemium"}{" "}
              подписка
            </p>
            <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <Metric label={view.quotaWindow === "TRIAL_TOTAL" ? "Общий запас на пробный период" : "Месячный объём"}>
                {formatTokens(view.monthlyDialogs)}
              </Metric>
              <Metric label={view.quotaWindow === "TRIAL_TOTAL" ? "Успешно использовано за пробный период" : "Успешно использовано"}>
                {formatTokens(view.usedDialogs)}
              </Metric>
              <Metric label="Зарезервировано до отправки">
                {formatTokens(view.reservedDialogs)}
              </Metric>
              <Metric label="Доступно">
                {formatTokens(view.availableDialogs)}
              </Metric>
              {view.subscription.kind !== "TRIAL" && (
                <>
                  <Metric label="Докуплено за период">
                    {formatTokens(view.topupGranted)}
                  </Metric>
                  <Metric label="Использовано из докупки">
                    {formatTokens(view.topupUsed)}
                  </Metric>
                  <Metric label="Осталось из докупки">
                    {formatTokens(view.topupRemaining)}
                  </Metric>
                </>
              )}
              <Metric label="Ожидают подтверждения">
                {formatTokens(view.pendingConfirmations.heldReservations)}
              </Metric>
              <Metric label="Неопределённые попытки">
                {formatTokens(view.pendingConfirmations.unknownAttempts)}
              </Metric>
              <Metric label="Попытки с неустановленным временем">
                {formatTokens(view.pendingConfirmations.timeUnresolvedAttempts)}
              </Metric>
            </dl>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <p>
                Подписка: {formatMoscowDateTime(view.subscription.startsAt)} —{" "}
                {formatMoscowDateTime(view.subscription.endsAt)} МСК
              </p>
              <p>
                {view.quotaWindow === "TRIAL_TOTAL" ? "Пробный срок" : `Период №${view.period.index + 1}`}:{" "}
                {formatMoscowDateTime(view.period.startsAt)} —{" "}
                {formatMoscowDateTime(view.period.endsAt)} МСК
              </p>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <PaidSnapshot
                title="Исходные оплаченные условия"
                snapshot={view.subscription.originalPaidSnapshot}
                priceLabel="Цена исходной подписки"
              />
              {upgraded && (
                <PaidSnapshot
                  title="Эффективные условия после повышения"
                  snapshot={effective ?? null}
                  priceLabel="Цена эффективных условий"
                />
              )}
            </div>
            {view.subscription.kind === "FREEMIUM" && (
              <p className="text-sm">
                Freemium имеет собственный 30-дневный период. Оплаченной цены и
                скидки нет.
              </p>
            )}
            {view.subscription.effectivePaidSnapshot &&
              currentCatalogTariff && (
                <p className="text-sm text-muted-foreground">
                  Текущая цена каталога: {currentCatalogTariff.unitPriceRub} ₽
                  за диалог. Оплаченные условия выше сохраняются.
                </p>
              )}
            {view.subscription.effectivePaidSnapshot &&
              catalog.data &&
              !currentCatalogTariff && (
                <p className="text-sm text-muted-foreground">
                  Тариф удалён из текущего каталога. Оплаченные условия выше
                  сохраняются.
                </p>
              )}
          </>
        )}
      </section>

      {view?.trial.activation && (
        <section className="panel space-y-2 p-4 text-sm" aria-label="Выданный пробный период">
          <h2 className="text-lg font-semibold">Выданный пробный период</h2>
          <p>{view.subscription.kind === "TRIAL" ? "Пробный период активен" : "Пробный период использован"}. Повторная активация невозможна.</p>
          <p>
            {view.trial.activation.snapshot.tariffName} · каталог v{view.trial.activation.snapshot.catalogVersion}.
            Выдано {formatTokens(view.trial.activation.snapshot.totalDialogs)} диалогов
            на {view.trial.activation.snapshot.durationDays} дней, без обновления каждые 30 дней.
          </p>
          <p>Начало: {formatMoscowDateTime(view.trial.activation.startsAt)} МСК.</p>
          <p>Плановое окончание: {formatMoscowDateTime(view.trial.activation.scheduledEndsAt)} МСК.</p>
          <p>{view.subscription.kind === "TRIAL" ? "Действует до" : "Завершён"}: {formatMoscowDateTime(view.trial.activation.actualEndsAt)} МСК.</p>
          <p className="text-muted-foreground">Условия зафиксированы при активации. Изменения каталога их не меняют.</p>
        </section>
      )}

      {paymentSummary.data && (
        <section className="panel space-y-2 p-4 text-sm">
          <h2 className="text-lg font-semibold">Сроки доступа</h2>
          <p>
            Оплачено до:{" "}
            {paymentSummary.data.billing.paidThroughAt
              ? `${formatMoscowDateTime(paymentSummary.data.billing.paidThroughAt)} МСК`
              : "нет оплаченного срока"}.
          </p>
          <p>
            {paymentSummary.data.billing.billing.subscription.kind === "TRIAL"
              ? `Пробный период действует до: ${formatMoscowDateTime(paymentSummary.data.billing.billing.subscription.endsAt)} МСК.`
              : paymentSummary.data.billing.segmentEndsAt
                ? `Текущий платный сегмент заканчивается: ${formatMoscowDateTime(paymentSummary.data.billing.segmentEndsAt)} МСК.`
                : "Нет текущего платного сегмента."}
          </p>
          {paymentSummary.data.billing.futureSegments.map((segment) => (
            <div key={segment.id} className="rounded-md border p-3">
              <p>
                Продление: {formatMoscowDateTime(segment.startsAt)} —{" "}
                {formatMoscowDateTime(segment.endsAt)} МСК.
              </p>
              <p>
                {segment.effectiveSnapshot.tariffName},{" "}
                {segment.effectiveSnapshot.monthlyDialogs} диалогов за 30 дней;
                цена диалога {segment.effectiveSnapshot.unitPriceRub} ₽.
              </p>
            </div>
          ))}
          <Link
            className="text-primary underline"
            to={`/accounts/${account.id}/payments`}
          >
            Платежи и продление по счёту
          </Link>
        </section>
      )}
      {paymentSummary.isError && (
        <p role="alert">
          Не удалось загрузить оплаченный горизонт.{" "}
          <button
            className={buttonClass}
            onClick={() => void paymentSummary.refetch()}
          >
            Повторить платежи
          </button>
        </p>
      )}
      {view && catalog.data && (
        <BillingTariffChangePanel key={`tariff-change:${account.id}`} accountId={account.id} billing={view} catalog={catalog.data} />
      )}
      {view && catalog.data && (
        <BillingOperationPanel
          key={account.id}
          accountId={account.id}
          billing={view}
          catalog={catalog.data}
        />
      )}
      {view && catalog.isError && !catalog.data && (
        <section className="panel p-4 text-sm" role="alert">
          Не удалось загрузить каталог операций.{" "}
          <button
            className={buttonClass}
            type="button"
            onClick={() => void catalog.refetch()}
          >
            Повторить
          </button>
        </section>
      )}

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Статус аккаунта</h2>
        {account.status === "ACTIVE" ? (
          <form className="mt-4" onSubmit={(event) => void suspend(event)}>
            <label className="block text-sm font-medium">
              <span className="mb-1.5 block">Причина приостановки</span>
              <textarea
                className="control min-h-24"
                value={suspensionReason}
                disabled={statusPending}
                onChange={(event) => setSuspensionReason(event.target.value)}
              />
            </label>
            <button
              className="mt-3 rounded-md bg-danger px-4 py-2 text-sm text-destructive-foreground disabled:opacity-50"
              type="submit"
              disabled={statusPending}
            >
              Приостановить аккаунт
            </button>
          </form>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Причина: {account.suspensionReason ?? "не указана"}
            </p>
            <button
              className={buttonClass}
              type="button"
              disabled={statusPending}
              onClick={() => void resume()}
            >
              Возобновить аккаунт
            </button>
          </div>
        )}
        {statusError && (
          <p className="mt-3 text-sm text-danger" role="alert">
            {statusError}
          </p>
        )}
      </section>
    </div>
  );
}
